import { createFileRoute, Link } from "@tanstack/react-router";
import { RequireAuth } from "@/components/RequireAuth";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { computeMetrics } from "@/lib/dashboard-metrics";
import type { Apontamento, ErrorType } from "@/lib/apontamento-rules";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertOctagon, Users, Factory, Table2, RefreshCcw, Stethoscope, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OverviewTab } from "@/components/dashboard/OverviewTab";
import { InconsistenciasTab } from "@/components/dashboard/InconsistenciasTab";
import { ColaboradoresTab } from "@/components/dashboard/ColaboradoresTab";
import { CentrosTab } from "@/components/dashboard/CentrosTab";
import { AuditTable } from "@/components/dashboard/AuditTable";
import { DiagnosticoTab } from "@/components/dashboard/DiagnosticoTab";
import { fmtDate } from "@/components/dashboard/shared";

export const Route = createFileRoute("/dashboard")({
  component: () => <RequireAuth><DashboardPage /></RequireAuth>,
});

const DASHBOARD_PAGE_SIZE = 1000;

async function fetchAllApontamentos() {
  const rows: Apontamento[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("apontamentos")
      .select("*", { count: "exact" })
      .order("inicio_real", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .range(from, from + DASHBOARD_PAGE_SIZE - 1);

    if (error) throw error;

    const batch = (data ?? []) as Apontamento[];
    rows.push(...batch);

    if (batch.length < DASHBOARD_PAGE_SIZE) break;
    from += DASHBOARD_PAGE_SIZE;
  }

  return rows;
}

function DashboardPage() {
  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["apontamentos"],
    queryFn: fetchAllApontamentos,
  });

  const m = useMemo(() => computeMetrics(rows), [rows]);
  const rowStatusErrors = useMemo(() => {
    const map = new Map<string, ErrorType[]>();
    for (const [id, errs] of m.audit.errors.entries()) {
      const rowLevel = errs.filter((err) => err !== "sem_intervalo");
      if (rowLevel.length > 0) map.set(id, rowLevel);
    }
    return map;
  }, [m.audit.errors]);
  const [errType, setErrType] = useState<ErrorType>("tempo_excessivo");
  const { roles } = useAuth();
  const canUpload = roles.includes("adm") || roles.includes("atualizador");

  return (
    <div className="space-y-6 max-w-[1720px] mx-auto">
      {/* Page heading */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="space-y-2">
          <Badge variant="secondary" className="h-6 rounded-md px-2.5 text-[11px] font-medium tracking-wide">
            Monitoramento operacional
          </Badge>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">Análise de Inconsistências de Apontamento</h1>
            {m.totalErrors > 0 && (
              <Badge variant="destructive" className="font-medium">
                {m.totalErrors.toLocaleString("pt-BR")} inconsistências
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {rows.length > 0
              ? <>Período: <span className="font-medium text-foreground">{m.periodo.inicio ? fmtDate(m.periodo.inicio, false) : "indisponível"}</span> até <span className="font-medium text-foreground">{m.periodo.fim ? fmtDate(m.periodo.fim, false) : "indisponível"}</span> · {m.colaboradoresUnicos} colaboradores identificados · {m.centrosUnicos} centros</>
              : "Faça upload de uma planilha para iniciar a análise."}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCcw className={isFetching ? "animate-spin" : ""} /> Atualizar
        </Button>
      </div>

      {(m.quality.missingNames > 0 || m.quality.missingDurations > 0 || m.quality.invalidDates > 0 || m.quality.unrealDates > 0) && (
        <Card className="border-warning/30 bg-warning/10">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground">Qualidade da importação comprometida</h2>
                <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
                  Parte dos indicadores pode ficar incompleta porque a planilha atual foi importada com colunas não reconhecidas ou datas inválidas.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {m.quality.missingNames > 0 && <Badge className="bg-card text-foreground border-border">Nomes ausentes: {m.quality.missingNames}</Badge>}
                {m.quality.missingDurations > 0 && <Badge className="bg-card text-foreground border-border">Durações ausentes: {m.quality.missingDurations}</Badge>}
                {m.quality.invalidDates > 0 && <Badge className="bg-card text-foreground border-border">Datas inválidas: {m.quality.invalidDates}</Badge>}
                {m.quality.unrealDates > 0 && <Badge className="bg-card text-foreground border-border">Datas irreais: {m.quality.unrealDates}</Badge>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <LoadingShell />
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <Tabs defaultValue="overview" className="space-y-5">
          <TabsList className="h-11 p-1 bg-card border border-border shadow-sm rounded-xl">
            <TabsTrigger value="overview" className="gap-2 px-3 text-[13px]">
              <Activity className="h-3.5 w-3.5" /> Visão Geral
            </TabsTrigger>
            <TabsTrigger value="erros" className="gap-2 px-3 text-[13px]">
              <AlertOctagon className="h-3.5 w-3.5" /> Inconsistências
              {m.totalErrors > 0 && (
                <span className="ml-1 text-[10px] font-semibold bg-destructive text-destructive-foreground rounded px-1.5 py-0.5 leading-none">
                  {m.totalErrors}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="diagnostico" className="gap-2 px-3 text-[13px]">
              <Stethoscope className="h-3.5 w-3.5" /> Diagnóstico
            </TabsTrigger>
            <TabsTrigger value="colaboradores" className="gap-2 px-3 text-[13px]">
              <Users className="h-3.5 w-3.5" /> Colaboradores
            </TabsTrigger>
            <TabsTrigger value="centros" className="gap-2 px-3 text-[13px]">
              <Factory className="h-3.5 w-3.5" /> Centros
            </TabsTrigger>
            <TabsTrigger value="apontamentos" className="gap-2 px-3 text-[13px]">
              <Table2 className="h-3.5 w-3.5" /> Apontamentos
            </TabsTrigger>
            {canUpload && (
              <Link
                to="/upload"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Upload className="h-3.5 w-3.5" /> Upload de Dados
              </Link>
            )}
          </TabsList>

          <TabsContent value="overview" className="mt-0 outline-none">
            <OverviewTab m={m} />
          </TabsContent>
          <TabsContent value="erros" className="mt-0 outline-none">
            <InconsistenciasTab rows={rows} m={m} type={errType} setType={setErrType} />
          </TabsContent>
          <TabsContent value="diagnostico" className="mt-0 outline-none">
            <DiagnosticoTab rows={rows} />
          </TabsContent>
          <TabsContent value="colaboradores" className="mt-0 outline-none">
            <ColaboradoresTab employees={m.employees} />
          </TabsContent>
          <TabsContent value="centros" className="mt-0 outline-none">
            <CentrosTab centers={m.centers} />
          </TabsContent>
          <TabsContent value="apontamentos" className="mt-0 outline-none">
            <Card>
              <CardContent className="p-5">
                <div className="mb-4">
                  <h3 className="text-[14px] font-semibold">Tabela completa de apontamentos</h3>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    Audite todos os registros; nesta grade o status considera apenas inconsistências da própria linha.
                  </p>
                </div>
                <AuditTable rows={rows} errorsMap={m.audit.errors} displayErrorsMap={rowStatusErrors} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}><CardContent className="p-5 h-[120px] animate-pulse bg-muted/30" /></Card>
        ))}
      </div>
      <Card><CardContent className="p-5 h-[300px] animate-pulse bg-muted/30" /></Card>
    </div>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-20 text-center">
        <div className="h-14 w-14 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
          <Activity className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-base font-semibold">Sem dados para analisar</h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
          Faça upload de uma planilha em <span className="font-medium text-foreground">Upload de Dados</span> para
          que o sistema identifique inconsistências, calcule indicadores e libere as visualizações.
        </p>
      </CardContent>
    </Card>
  );
}
